import {gql, useQuery} from '@apollo/client'
import {AnalyticsContext, NFThemeProvider} from 'nf-ui'
import React, {FC, useEffect} from 'react'
import {Redirect, Route, Switch, useRouteMatch} from 'react-router'
import {PageLoading} from './components/PageLoading'
import {useIdentifyUser} from './components/useIdentifyUser'
import {useOrganisationIdStr} from './components/useOrganisationIdStr'
import {UserSettings} from './components/UserSettingsContext'
import {AdminlandRoot} from './pages/Adminland/AdminlandRoot'
import {UserlandRoot} from './pages/Userland/UserlandRoot'
import {useDailySession} from './useDailySession'
import {useSetCloudfrontCookies} from './useSetCloudfrontCookies'
import {OrganisationIdRoutesData} from './__types__/OrganisationIdRoutesData'

const ORGANISTION_ID_DATA = gql`
    query OrganisationIdRoutesData {
        organisations {
            idStr
            name
            accentColor
        }
    }
`

export type OrganisationIdRootProps = {
    organisationId: string
}

export const OrganisationIdRoot: FC<OrganisationIdRootProps> = ({organisationId}) => {
    const {path} = useRouteMatch()!
    const {data, loading} = useQuery<OrganisationIdRoutesData>(ORGANISTION_ID_DATA)
    const {userSettings, setUserSettings} = UserSettings.useContainer()
    const currentOrganisationIdStr = useOrganisationIdStr()

    useSetCloudfrontCookies()
    useIdentifyUser()
    useDailySession()

    useEffect(() => {
        if (userSettings.currentOrganisation !== organisationId) {
            setUserSettings({
                ...userSettings,
                currentOrganisation: organisationId,
            })
        }
    }, [organisationId, userSettings, setUserSettings])

    if (!data || loading) return <PageLoading />
    if (currentOrganisationIdStr !== organisationId) return <PageLoading />

    if (!data.organisations.length) {
        return <Redirect to="/" />
    }

    if (!data.organisations.some((o) => o.idStr === organisationId)) {
        return <Redirect to={`/${data.organisations[0].idStr}`} />
    }

    const org = data.organisations.find((organisation) => organisation.idStr === organisationId)

    return (
        <Switch>
            <Route path={`${path}/admin`}>
                <AnalyticsContext.Provider value={{platform: 'WebAdminland'}}>
                    <AdminlandRoot />
                </AnalyticsContext.Provider>
            </Route>
            <Route default>
                <AnalyticsContext.Provider value={{platform: 'WebUserland'}}>
                    <NFThemeProvider primaryColor={org ? org.accentColor : undefined}>
                        <UserlandRoot />
                    </NFThemeProvider>
                </AnalyticsContext.Provider>
            </Route>
        </Switch>
    )
}
